/**
 * QMS Phase 1: Policy Documents & Structure
 * List policy documents (e.g. ORDIN 1761), edit structure tree, manage requirements.
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  usePolicyDocuments,
  usePolicyDocument,
  usePolicyStructureTree,
  useRequirementsByPolicy,
  useRequirementsBySourceNode,
  useQmsPolicyMutations,
} from '../../hooks/useQmsPolicy';
import { useImplementedByStandards } from '../../hooks/useQmsStandards';
import { usePolicyFrameworks } from '../../hooks/usePolicyFrameworks';
import Modal from '../ui/Modal';
import { useNotifications } from '../NotificationSystem';
import './PolicyDocumentManager.css';

const NODE_TYPES = ['chapter', 'article', 'section', 'subsection', 'annex', 'paragraph'];
const COMPLIANCE_LEVELS = ['MUST', 'SHOULD', 'MAY'];

const PolicyDocumentManager = ({ organizationId, userId, userName }) => {
  const { t } = useTranslation('qms');
  const { notify } = useNotifications();
  const { documents, loading, error, refresh } = usePolicyDocuments({ status: 'active' });
  const { frameworks: policyFrameworks } = usePolicyFrameworks(organizationId);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [showDocForm, setShowDocForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [docForm, setDocForm] = useState({
    code: '',
    title: '',
    issuingAuthority: '',
    documentType: 'ordinance',
    legalForce: 'mandatory',
    status: 'active',
    policyFrameworkId: '',
    policyFrameworkName: '',
  });
  const [showNodeForm, setShowNodeForm] = useState(false);
  const [nodeForm, setNodeForm] = useState({
    parentId: null,
    parentPath: '',
    nodeType: 'section',
    nodeNumber: '',
    nodeTitle: '',
    nodeContent: '',
    orderIndex: 0,
  });
  const [showReqForm, setShowReqForm] = useState(false);
  const [reqForm, setReqForm] = useState({
    code: '',
    requirementText: '',
    complianceLevel: 'MUST',
    targetAudience: '',
    verificationMethod: '',
    sourcePolicyStructureId: null,
  });
  const [activeTab, setActiveTab] = useState('structure'); // 'structure' | 'requirements'
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodeDrawer, setShowNodeDrawer] = useState(false);

  const {
    document: selectedDoc,
    loading: docLoading,
    refresh: refreshDoc,
  } = usePolicyDocument(selectedDocId);
  const { tree, loading: treeLoading, refresh: refreshTree } = usePolicyStructureTree(selectedDocId);
  const { requirements, loading: reqLoading, refresh: refreshReqs } = useRequirementsByPolicy(selectedDocId);
  const requirementIds = useMemo(() => requirements.map((r) => r.id), [requirements]);
  const { implementedByMap } = useImplementedByStandards(requirementIds);
  const mutations = useQmsPolicyMutations();

  const structureNodeById = useMemo(() => {
    const map = {};
    const walk = (nodes) => {
      (nodes || []).forEach((n) => {
        map[n.id] = { nodeNumber: n.nodeNumber, nodeTitle: n.nodeTitle, nodeType: n.nodeType };
        walk(n.children);
      });
    };
    walk(tree);
    return map;
  }, [tree]);

  const groupedDocuments = useMemo(() => {
    const groups = {};
    const noFrameworkDocs = [];

    documents.forEach((doc) => {
      const frameworkId = doc.policyFrameworkId;
      if (!frameworkId) {
        noFrameworkDocs.push(doc);
      } else {
        if (!groups[frameworkId]) {
          const framework = policyFrameworks.find((f) => f.id === frameworkId);
          groups[frameworkId] = {
            id: frameworkId,
            name: doc.policyFrameworkName || framework?.name || frameworkId,
            documents: [],
          };
        }
        groups[frameworkId].documents.push(doc);
      }
    });

    const sortedGroups = Object.values(groups).sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    if (noFrameworkDocs.length > 0) {
      sortedGroups.push({
        id: 'no-framework',
        name: t('noPolicyFramework'),
        documents: noFrameworkDocs,
      });
    }

    const initialExpandedState = {};
    sortedGroups.forEach((group) => {
      initialExpandedState[group.id] = true;
    });
    setExpandedGroups((prev) => {
      const merged = { ...initialExpandedState };
      Object.keys(prev).forEach((key) => {
        if (merged[key] !== undefined) {
          merged[key] = prev[key];
        }
      });
      return merged;
    });

    return sortedGroups;
  }, [documents, policyFrameworks, t]);

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleNodeClick = useCallback((node) => {
    if (selectedNode?.id === node.id) {
      setSelectedNode(null);
      setShowNodeDrawer(false);
    } else {
      setSelectedNode(node);
      setShowNodeDrawer(true);
    }
  }, [selectedNode]);

  const closeDrawer = useCallback(() => {
    setShowNodeDrawer(false);
    setTimeout(() => setSelectedNode(null), 300);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showNodeDrawer) {
        closeDrawer();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showNodeDrawer, closeDrawer]);

  const handleCreateDoc = () => {
    setEditingDoc(null);
    setDocForm({
      code: '',
      title: '',
      issuingAuthority: '',
      documentType: 'ordinance',
      legalForce: 'mandatory',
      status: 'active',
      policyFrameworkId: '',
      policyFrameworkName: '',
    });
    setShowDocForm(true);
  };

  const handleEditDoc = () => {
    if (!selectedDoc) return;
    setEditingDoc(selectedDoc);
    setDocForm({
      code: selectedDoc.code || '',
      title: selectedDoc.title || '',
      issuingAuthority: selectedDoc.issuingAuthority || '',
      documentType: selectedDoc.documentType || 'ordinance',
      legalForce: selectedDoc.legalForce || 'mandatory',
      status: selectedDoc.status || 'active',
      policyFrameworkId: selectedDoc.policyFrameworkId || '',
      policyFrameworkName: selectedDoc.policyFrameworkName || '',
    });
    setShowDocForm(true);
  };

  const handleSaveDoc = async () => {
    if (!docForm.code || !docForm.title) {
      notify.error(t('policyDocCodeTitleRequired'));
      return;
    }
    try {
      const payload = {
        ...docForm,
        organizationId: organizationId || null,
        policyFrameworkId: docForm.policyFrameworkId || null,
        policyFrameworkName: docForm.policyFrameworkName || null,
      };
      if (editingDoc) {
        await mutations.updatePolicyDocument(
          editingDoc.id,
          payload,
          userId,
          userName
        );
        notify.success(t('policyDocUpdated'));
      } else {
        await mutations.createPolicyDocument(
          payload,
          userId,
          userName
        );
        notify.success(t('policyDocCreated'));
      }
      setShowDocForm(false);
      refresh();
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    }
  };

  const handleAddNode = (parentId = null, parentPath = '', orderIndex = 0) => {
    setNodeForm({
      parentId,
      parentPath,
      nodeType: 'section',
      nodeNumber: '',
      nodeTitle: '',
      nodeContent: '',
      orderIndex,
    });
    setShowNodeForm(true);
  };

  const handleSaveNode = async () => {
    if (!selectedDocId) return;
    try {
      await mutations.createStructureNode(
        {
          policyDocumentId: selectedDocId,
          ...nodeForm,
        },
        userId,
        userName
      );
      notify.success(t('structureNodeAdded'));
      setShowNodeForm(false);
      refreshTree();
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    }
  };

  const handleAddRequirement = (sourcePolicyStructureId = null) => {
    setReqForm({
      code: '',
      requirementText: '',
      complianceLevel: 'MUST',
      targetAudience: '',
      verificationMethod: '',
      sourcePolicyStructureId,
    });
    setShowReqForm(true);
  };

  const handleSaveRequirement = async () => {
    if (!selectedDocId || !reqForm.code || !reqForm.requirementText) {
      notify.error(t('requirementCodeTextRequired'));
      return;
    }
    try {
      await mutations.createRequirement(
        {
          policyDocumentId: selectedDocId,
          ...reqForm,
        },
        userId,
        userName
      );
      notify.success(t('requirementAdded'));
      setShowReqForm(false);
      refreshReqs();
    } catch (err) {
      notify.error(err.message || t('saveFailed'));
    }
  };

  if (loading) {
    return (
      <div className="policy-document-manager loading">
        <p>{t('loadingPolicyDocuments')}</p>
      </div>
    );
  }

  return (
    <div className="policy-document-manager">
      <div className="manager-header">
        <h2>{t('policyDocuments')}</h2>
        <button className="btn btn-primary" onClick={handleCreateDoc}>
          {t('newPolicyDocument')}
        </button>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="content-layout">
        <div className="documents-list">
          <h3>{t('documentsList')}</h3>
          {documents.length === 0 ? (
            <p className="empty-state">{t('noPolicyDocuments')}</p>
          ) : (
            <div className="documents-accordion">
              {groupedDocuments.map((group) => {
                const isExpanded = expandedGroups[group.id];
                const isNoFrameworkGroup = group.id === 'no-framework';
                return (
                  <div key={group.id} className="document-group">
                    <div
                      className={`group-header ${isNoFrameworkGroup ? 'no-framework' : ''}`}
                      onClick={() => toggleGroup(group.id)}
                    >
                      <span className="group-toggle">
                        {isExpanded ? '▼' : '▶'}
                      </span>
                      <span className="group-name">{group.name}</span>
                      <span className="group-count">({group.documents.length})</span>
                    </div>
                    {isExpanded && (
                      <ul className="group-documents">
                        {group.documents.map((doc) => {
                          const hasTitle = (doc.name || doc.title) && (doc.name || doc.title).trim();
                          const primaryTitle = hasTitle ? (doc.name || doc.title) : doc.code;
                          const subtitle = doc.shortName || (hasTitle ? doc.code : null);
                          return (
                            <li
                              key={doc.id}
                              className={selectedDocId === doc.id ? 'selected' : ''}
                              onClick={() => setSelectedDocId(doc.id)}
                            >
                              <span className="doc-code">{primaryTitle}</span>
                              {subtitle && <span className="doc-title">{subtitle}</span>}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={`document-detail ${!selectedDocId ? 'empty' : ''}`}>
          {!selectedDocId ? (
            <p className="empty-state">{t('selectDocument')}</p>
          ) : (
            <>
              {docLoading ? (
                <p>{t('loading')}</p>
              ) : (
                <>
                  <div className="detail-header">
                    <div className="detail-title-block">
                      <h3>
                        {selectedDoc?.name || selectedDoc?.title || selectedDoc?.code}
                        {' '}
                        ({selectedDoc?.code})
                      </h3>
                      <div className="detail-framework">
                        <span className="detail-framework-label">{t('policyFramework')}:</span>
                        <span
                          className={`detail-framework-value ${selectedDoc?.policyFrameworkName || policyFrameworks.find((f) => f.id === selectedDoc?.policyFrameworkId)?.name ? 'has-framework' : 'no-framework'}`}
                        >
                          {selectedDoc?.policyFrameworkName ||
                            policyFrameworks.find((f) => f.id === selectedDoc?.policyFrameworkId)?.name ||
                            '—'}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={handleEditDoc}
                    >
                      {t('editPolicyDocument')}
                    </button>
                  </div>
                  <div className="tabs">
                    <button
                      className={activeTab === 'structure' ? 'active' : ''}
                      onClick={() => setActiveTab('structure')}
                    >
                      {t('structureTree')}
                    </button>
                    <button
                      className={activeTab === 'requirements' ? 'active' : ''}
                      onClick={() => setActiveTab('requirements')}
                    >
                      {t('requirements')}
                    </button>
                  </div>

                  {activeTab === 'structure' && (
                    <div className="structure-panel">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleAddNode(null, '', 0)}
                      >
                        {t('addRootNode')}
                      </button>
                      {treeLoading ? (
                        <p>{t('loading')}</p>
                      ) : (
                        <div className="structure-tree-wrap">
                          <ul className="structure-tree" role="tree" aria-label={t('structureTree')}>
                            {tree.map((node) => (
                              <TreeNode
                                key={node.id}
                                node={node}
                                depth={0}
                                onAddChild={handleAddNode}
                                onNodeClick={handleNodeClick}
                                selectedNodeId={selectedNode?.id}
                              />
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'requirements' && (
                    <div className="requirements-panel">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleAddRequirement(null)}
                      >
                        {t('addRequirement')}
                      </button>
                      {reqLoading ? (
                        <p>{t('loading')}</p>
                      ) : (
                        (() => {
                          const grouped = requirements.reduce((acc, r) => {
                            const key = r.sourcePolicyStructureId || `none-${r.id}`;
                            if (!acc[key]) acc[key] = [];
                            acc[key].push(r);
                            return acc;
                          }, {});
                          const groupOrder = [];
                          const seen = new Set();
                          requirements.forEach((r) => {
                            const key = r.sourcePolicyStructureId || `none-${r.id}`;
                            if (!seen.has(key)) {
                              seen.add(key);
                              groupOrder.push(key);
                            }
                          });
                          return (
                            <table className="requirements-table">
                              <thead>
                                <tr>
                                  <th>{t('code')}</th>
                                  <th>{t('policySource')}</th>
                                  <th>{t('requirementText')}</th>
                                  <th>{t('complianceLevel')}</th>
                                  <th>{t('targetAudience')}</th>
                                  <th>{t('implementedByStandards')}</th>
                                </tr>
                              </thead>
                              {groupOrder.map((key) => {
                                const groupReqs = grouped[key];
                                const sourceNode =
                                  groupReqs[0]?.sourcePolicyStructureId &&
                                  structureNodeById[groupReqs[0].sourcePolicyStructureId];
                                return (
                                  <tbody key={key} className="requirement-group">
                                    {groupReqs.map((r) => {
                                      const node =
                                        r.sourcePolicyStructureId &&
                                        structureNodeById[r.sourcePolicyStructureId];
                                      return (
                                        <tr key={r.id}>
                                          <td>{r.code}</td>
                                          <td className="req-source-cell">
                                            {node ? (
                                              <span title={node.nodeTitle}>
                                                <strong>{node.nodeNumber}</strong>
                                                {node.nodeTitle ? ` — ${node.nodeTitle}` : ''}
                                              </span>
                                            ) : (
                                              '—'
                                            )}
                                          </td>
                                          <td className="req-text">{r.requirementText}</td>
                                          <td>
                                            <span className={`badge badge-${r.complianceLevel?.toLowerCase()}`}>
                                              {r.complianceLevel}
                                            </span>
                                          </td>
                                          <td>{r.targetAudience || '—'}</td>
                                          <td className="implemented-by-cell">
                                            {(implementedByMap[r.id] || []).length > 0 ? (
                                              <span className="implemented-by-list" title={(implementedByMap[r.id] || []).map((s) => s.standardName).join(', ')}>
                                                {(implementedByMap[r.id] || []).map((s) => (
                                                  <span key={s.standardId} className="implemented-by-badge">{s.standardName}</span>
                                                ))}
                                              </span>
                                            ) : (
                                              '—'
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                );
                              })}
                            </table>
                          );
                        })()
                      )}
                      {requirements.length === 0 && !reqLoading && (
                        <p className="empty-state">{t('noRequirements')}</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* New/Edit Policy Document Modal */}
      <Modal
        isOpen={showDocForm}
        onClose={() => setShowDocForm(false)}
        title={editingDoc ? t('editPolicyDocument') : t('newPolicyDocument')}
      >
        <div className="form">
          <div className="form-group">
            <label>{t('code')} *</label>
            <input
              value={docForm.code}
              onChange={(e) => setDocForm({ ...docForm, code: e.target.value })}
              placeholder="ORDIN-1761-2022"
            />
          </div>
          <div className="form-group">
            <label>{t('title')} *</label>
            <input
              value={docForm.title}
              onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
              placeholder={t('documentTitlePlaceholder')}
            />
          </div>
          <div className="form-group">
            <label>{t('issuingAuthority')}</label>
            <input
              value={docForm.issuingAuthority}
              onChange={(e) =>
                setDocForm({ ...docForm, issuingAuthority: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>{t('documentType')}</label>
            <select
              value={docForm.documentType}
              onChange={(e) =>
                setDocForm({ ...docForm, documentType: e.target.value })
              }
            >
              <option value="ordinance">{t('nomenclatures:options.ordinance')}</option>
              <option value="annex">{t('nomenclatures:options.annex')}</option>
              <option value="guideline">{t('nomenclatures:options.guideline')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('legalForce')}</label>
            <select
              value={docForm.legalForce}
              onChange={(e) =>
                setDocForm({ ...docForm, legalForce: e.target.value })
              }
            >
              <option value="mandatory">{t('nomenclatures:options.mandatory')}</option>
              <option value="recommended">{t('nomenclatures:options.recommended')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('policyFramework')}</label>
            <select
              value={docForm.policyFrameworkId || ''}
              onChange={(e) => {
                const id = e.target.value || '';
                const fw = policyFrameworks.find((f) => f.id === id);
                setDocForm({
                  ...docForm,
                  policyFrameworkId: id,
                  policyFrameworkName: fw ? fw.name : '',
                });
              }}
            >
              <option value="">{t('noPolicyFramework')}</option>
              {policyFrameworks.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name || f.code || f.id}
                </option>
              ))}
            </select>
            <span className="form-hint">{t('policyFrameworkHint')}</span>
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setShowDocForm(false)}>
              {t('cancel')}
            </button>
            <button className="btn btn-primary" onClick={handleSaveDoc}>
              {t('save')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Structure Node Modal */}
      <Modal
        isOpen={showNodeForm}
        onClose={() => setShowNodeForm(false)}
        title={t('addStructureNode')}
      >
        <div className="form">
          <div className="form-group">
            <label>{t('nodeType')}</label>
            <select
              value={nodeForm.nodeType}
              onChange={(e) =>
                setNodeForm({ ...nodeForm, nodeType: e.target.value })
              }
            >
              {NODE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`nomenclatures:options.${type}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('nodeNumber')}</label>
            <input
              value={nodeForm.nodeNumber}
              onChange={(e) =>
                setNodeForm({ ...nodeForm, nodeNumber: e.target.value })
              }
              placeholder="1.1, Art. 3"
            />
          </div>
          <div className="form-group">
            <label>{t('nodeTitle')}</label>
            <input
              value={nodeForm.nodeTitle}
              onChange={(e) =>
                setNodeForm({ ...nodeForm, nodeTitle: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>{t('nodeContent')}</label>
            <textarea
              value={nodeForm.nodeContent}
              onChange={(e) =>
                setNodeForm({ ...nodeForm, nodeContent: e.target.value })
              }
              rows={4}
            />
          </div>
          <div className="form-group">
            <label>{t('orderIndex')}</label>
            <input
              type="number"
              value={nodeForm.orderIndex}
              onChange={(e) =>
                setNodeForm({
                  ...nodeForm,
                  orderIndex: parseInt(e.target.value, 10) || 0,
                })
              }
            />
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setShowNodeForm(false)}>
              {t('cancel')}
            </button>
            <button className="btn btn-primary" onClick={handleSaveNode}>
              {t('save')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Requirement Modal */}
      <Modal
        isOpen={showReqForm}
        onClose={() => setShowReqForm(false)}
        title={t('addRequirement')}
      >
        <div className="form">
          <div className="form-group">
            <label>{t('code')} *</label>
            <input
              value={reqForm.code}
              onChange={(e) => setReqForm({ ...reqForm, code: e.target.value })}
              placeholder="ORDIN-1761-REQ-1.8.001"
            />
          </div>
          <div className="form-group">
            <label>{t('requirementText')} *</label>
            <textarea
              value={reqForm.requirementText}
              onChange={(e) =>
                setReqForm({ ...reqForm, requirementText: e.target.value })
              }
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>{t('complianceLevel')}</label>
            <select
              value={reqForm.complianceLevel}
              onChange={(e) =>
                setReqForm({ ...reqForm, complianceLevel: e.target.value })
              }
            >
              {COMPLIANCE_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('targetAudience')}</label>
            <input
              value={reqForm.targetAudience}
              onChange={(e) =>
                setReqForm({ ...reqForm, targetAudience: e.target.value })
              }
            />
          </div>
          <div className="form-group">
            <label>{t('verificationMethod')}</label>
            <input
              value={reqForm.verificationMethod}
              onChange={(e) =>
                setReqForm({ ...reqForm, verificationMethod: e.target.value })
              }
            />
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setShowReqForm(false)}>
              {t('cancel')}
            </button>
            <button className="btn btn-primary" onClick={handleSaveRequirement}>
              {t('save')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Node Detail Drawer */}
      {showNodeDrawer && selectedNode && (
        <NodeDrawer
          node={selectedNode}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
};

function TreeNode({ node, depth = 0, onAddChild, onNodeClick, selectedNodeId }) {
  const { t } = useTranslation('qms');
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;
  const parentPath = node.materializedPath || '';
  const nextOrder = node.children?.length || 0;
  const isSelected = selectedNodeId === node.id;

  return (
    <li
      className={`tree-node tree-node--depth-${depth} tree-node--${node.nodeType} ${isSelected ? 'tree-node--selected' : ''}`}
      role="treeitem"
      aria-expanded={hasChildren ? expanded : undefined}
      style={{ '--depth': depth }}
    >
      <div 
        className="tree-node-row"
        onClick={() => onNodeClick && onNodeClick(node)}
      >
        <span
          className="tree-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {hasChildren ? (expanded ? '▼' : '▶') : <span className="tree-dot" aria-hidden>·</span>}
        </span>
        <span className={`node-type-badge node-type-badge--${node.nodeType}`}>
          {node.nodeType}
        </span>
        <span className="node-number">{node.nodeNumber}</span>
        <span className="node-title">{node.nodeTitle || '(no title)'}</span>
        <button
          type="button"
          className="btn btn-xs btn-add-node"
          onClick={(e) => {
            e.stopPropagation();
            onAddChild(node.id, parentPath, nextOrder);
          }}
          title={t('addStructureNode')}
        >
          +
        </button>
      </div>
      {hasChildren && expanded && (
        <ul className="tree-children" role="group">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              onAddChild={onAddChild}
              onNodeClick={onNodeClick}
              selectedNodeId={selectedNodeId}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function NodeDrawer({ node, onClose }) {
  const { t } = useTranslation('qms');
  const { requirements, loading: reqLoading } = useRequirementsBySourceNode(node.id);
  const [showMetadata, setShowMetadata] = useState(false);
  const [expandedReqs, setExpandedReqs] = useState({});

  const toggleReqElaboration = (reqId) => {
    setExpandedReqs((prev) => ({
      ...prev,
      [reqId]: !prev[reqId],
    }));
  };

  const getNodeTypeLabel = (nodeType) => {
    const labels = {
      reference: 'REFERINȚĂ',
      standard: 'STANDARD',
      criterion: 'CRITERIU',
      chapter: 'CAPITOL',
      article: 'ARTICOL',
      section: 'SECȚIUNE',
      subsection: 'SUBSECȚIUNE',
      annex: 'ANEXĂ',
      paragraph: 'PARAGRAF',
    };
    return labels[nodeType] || nodeType.toUpperCase();
  };

  const getNodeTypeBadgeClass = (nodeType) => {
    const classes = {
      reference: 'node-badge-reference',
      standard: 'node-badge-standard',
      criterion: 'node-badge-criterion',
      section: 'node-badge-section',
      subsection: 'node-badge-subsection',
      chapter: 'node-badge-chapter',
      article: 'node-badge-article',
    };
    return classes[nodeType] || 'node-badge-default';
  };

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className="drawer-container">
        <div className="drawer-header">
          <div className="drawer-header-left">
            <span className={`node-type-badge-large ${getNodeTypeBadgeClass(node.nodeType)}`}>
              {getNodeTypeLabel(node.nodeType)}
            </span>
            <span className="node-number-large">{node.nodeNumber}</span>
            <span className="node-title-large">{node.nodeTitle || '(fără titlu)'}</span>
          </div>
          <button
            type="button"
            className="drawer-close-btn"
            onClick={onClose}
            aria-label="Închide"
          >
            ✕
          </button>
        </div>

        <div className="drawer-content">
          {node.nodeContent && (
            <section className="drawer-section">
              <h3 className="drawer-section-title">Descriere</h3>
              <div className="drawer-section-body">
                <p className="node-content-text">{node.nodeContent}</p>
              </div>
            </section>
          )}

          <section className="drawer-section">
            <h3 className="drawer-section-title">
              Cerințe
              <span className="count-badge">{requirements.length}</span>
            </h3>
            <div className="drawer-section-body">
              {reqLoading ? (
                <div className="drawer-loading">
                  <div className="spinner" />
                  <span>Se încarcă cerințele...</span>
                </div>
              ) : requirements.length === 0 ? (
                <p className="empty-state-text">Nicio cerință directă pentru acest nod.</p>
              ) : (
                <div className="requirements-list">
                  {requirements.map((req) => {
                    const isExpanded = expandedReqs[req.id];
                    const hasElaboration = req.elaboration && req.elaboration.trim();
                    return (
                      <div key={req.id} className="requirement-card">
                        <div className="requirement-header">
                          <span className="requirement-code-badge">{req.code}</span>
                          <p className="requirement-text">{req.requirementText}</p>
                        </div>
                        {hasElaboration && (
                          <div className="requirement-elaboration-section">
                            <button
                              type="button"
                              className="elaboration-toggle"
                              onClick={() => toggleReqElaboration(req.id)}
                            >
                              Detalii {isExpanded ? '▲' : '▼'}
                            </button>
                            {isExpanded && (
                              <div className="elaboration-content">
                                {req.elaboration}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section className="drawer-section drawer-metadata-section">
            <button
              type="button"
              className="metadata-toggle"
              onClick={() => setShowMetadata(!showMetadata)}
            >
              Informații tehnice {showMetadata ? '▲' : '▼'}
            </button>
            {showMetadata && (
              <div className="metadata-content">
                <div className="metadata-row">
                  <span className="metadata-label">Policy Document ID:</span>
                  <span className="metadata-value">{node.policyDocumentId}</span>
                </div>
                <div className="metadata-row">
                  <span className="metadata-label">Materialized Path:</span>
                  <span className="metadata-value">{node.materializedPath || '—'}</span>
                </div>
                <div className="metadata-row">
                  <span className="metadata-label">Order Index:</span>
                  <span className="metadata-value">{node.orderIndex}</span>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

export default PolicyDocumentManager;
